'use client';

import * as React from 'react';
import { cn } from './cn';
import { useControllableState, useElementDirection, useId } from './hooks';

type TabsVariant = 'underline' | 'pill' | 'bar';

interface TabsCtx {
  value: string;
  setValue: (v: string) => void;
  baseId: string;
  orientation: 'horizontal' | 'vertical';
  variant: TabsVariant;
}
const Ctx = React.createContext<TabsCtx | null>(null);
const useTabs = () => {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error('Tabs.* must be rendered inside <Tabs>');
  return ctx;
};

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  /** `underline` for panels, `pill` for filter rows, `bar` for the mobile bottom tab bar. */
  variant?: TabsVariant;
}

/**
 * WAI-ARIA tab pattern with roving tabindex. Arrow keys follow the *reading*
 * direction, so in Hebrew `←` moves to the next tab.
 */
export function Tabs({
  value,
  defaultValue = '',
  onValueChange,
  orientation = 'horizontal',
  variant = 'underline',
  className,
  children,
  ...props
}: TabsProps) {
  const baseId = useId('tabs');
  const [current, setCurrent] = useControllableState(value, defaultValue, onValueChange);
  const ctx = React.useMemo(
    () => ({ value: current, setValue: setCurrent, baseId, orientation, variant }),
    [current, setCurrent, baseId, orientation, variant],
  );
  return (
    <Ctx.Provider value={ctx}>
      <div
        data-orientation={orientation}
        className={cn('flex min-h-0', orientation === 'horizontal' ? 'flex-col' : 'flex-row', className)}
        {...props}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
}

const LIST_VARIANT: Record<TabsVariant, string> = {
  underline: 'gap-1 border-b border-line',
  pill: 'gap-1.5',
  bar: 'w-full justify-around border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]',
};

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Horizontal scroll with fading edges — for long tab rows on phones. */
  scrollable?: boolean;
  'aria-label'?: string;
}

export const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { className, scrollable, children, ...props },
  ref,
) {
  const { orientation, variant } = useTabs();
  const listRef = React.useRef<HTMLDivElement>(null);
  const dir = useElementDirection(listRef);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const tabs = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])') ?? [],
    );
    if (tabs.length === 0) return;
    const i = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    const next = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const prev = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    const forwardKeys = orientation === 'vertical' ? ['ArrowDown'] : [next, 'ArrowDown'];
    const backKeys = orientation === 'vertical' ? ['ArrowUp'] : [prev, 'ArrowUp'];

    let target: HTMLButtonElement | undefined;
    if (forwardKeys.includes(e.key)) target = tabs[(i + 1) % tabs.length];
    else if (backKeys.includes(e.key)) target = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') target = tabs[0];
    else if (e.key === 'End') target = tabs[tabs.length - 1];
    if (!target) return;
    e.preventDefault();
    target.focus();
    target.click();
  };

  return (
    <div
      ref={(node) => {
        listRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={onKeyDown}
      className={cn(
        'flex shrink-0',
        orientation === 'vertical' ? 'flex-col items-stretch' : 'items-center',
        LIST_VARIANT[variant],
        scrollable && 'overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  icon?: React.ReactNode;
  /** Count / status chip after the label. */
  badge?: React.ReactNode;
}

const TRIGGER_VARIANT: Record<TabsVariant, { base: string; on: string; off: string }> = {
  underline: {
    base: 'min-h-touch gap-2 px-3 text-sm font-medium -mb-px border-b-2',
    on: 'border-accent text-ink',
    off: 'border-transparent text-ink-3 hover:text-ink hover:border-line-strong',
  },
  pill: {
    base: 'min-h-9 gap-2 rounded-full px-3.5 text-sm font-medium k-hit',
    on: 'bg-accent-soft text-accent',
    off: 'text-ink-2 hover:bg-paper-3',
  },
  bar: {
    base: 'min-h-tabbar flex-1 flex-col gap-1 rounded-lg px-1 text-2xs font-medium [&_svg]:size-5',
    on: 'text-accent',
    off: 'text-ink-3 hover:text-ink-2',
  },
};

export const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(
  { value, icon, badge, className, children, disabled, ...props },
  ref,
) {
  const { value: current, setValue, baseId, variant } = useTabs();
  const selected = current === value;
  const v = TRIGGER_VARIANT[variant];
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      data-state={selected ? 'active' : 'inactive'}
      onClick={() => setValue(value)}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap',
        'transition-colors duration-(--duration-fast) ease-out-soft',
        'disabled:pointer-events-none disabled:opacity-45',
        v.base,
        selected ? v.on : v.off,
        className,
      )}
      {...props}
    >
      {icon}
      {children}
      {badge}
    </button>
  );
});

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Keep the panel mounted when inactive (preserves scroll position and inputs). */
  keepMounted?: boolean;
}

export const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(function TabsContent(
  { value, keepMounted, className, children, ...props },
  ref,
) {
  const { value: current, baseId } = useTabs();
  const selected = current === value;
  if (!selected && !keepMounted) return null;
  return (
    <div
      ref={ref}
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      hidden={!selected}
      tabIndex={0}
      className={cn('min-h-0 flex-1 focus-visible:outline-none', selected && 'animate-fade-in', className)}
      {...props}
    >
      {children}
    </div>
  );
});
